/**
 * Returns a user's first and last name as one string.
 *
 * @param {{ first?: string|null, last?: string|null, email?: string|null }|null|undefined} user User object.
 * @returns {string} Full name, or an empty string when no name is set.
 */
export const getUserFullName = (user) => {
  return [user?.first, user?.last].filter(Boolean).join(" ").trim();
};

/**
 * Returns the display name for a user-like object with email fallback.
 *
 * @param {{ first?: string|null, last?: string|null, email?: string|null }|null|undefined} user User object.
 * @param {string} fallback Fallback label when name and email are missing.
 * @returns {string} User display name.
 */
export const getUserDisplayName = (user, fallback = "Unknown User") => {
  return getUserFullName(user) || user?.email || fallback;
};

/**
 * Returns compact initials for a user-like object with email fallback.
 *
 * @param {{ first?: string|null, last?: string|null, email?: string|null }|null|undefined} user User object.
 * @param {Object} options Initials options.
 * @param {string} options.fallback Fallback initials when no name or email is set.
 * @param {boolean} options.useEmailFallback Whether to use the email prefix as fallback initials.
 * @returns {string} User initials.
 */
export const getUserInitials = (
  user,
  { fallback = "U", useEmailFallback = true } = {}
) => {
  const firstInitial = user?.first?.[0] || "";
  const lastInitial = user?.last?.[0] || "";
  const initials = `${firstInitial}${lastInitial}`.toUpperCase();

  if (initials) {
    return initials;
  }

  if (!useEmailFallback) {
    return fallback;
  }

  const [emailName = ""] = (user?.email || "").split("@");
  return emailName.slice(0, 2).toUpperCase() || fallback;
};
